<template>
  <div class="flex h-13">
    <div class="flex pl-6 analytics-first-col-sticky bg-slate-100">
      <div
        class="flex items-center border-b border-l border-r bg-pure-white border-slate-400"
        :class="{ 'rounded-bl': isLast }"
      >
        <div class="flex flex-shrink-0 w-20 px-4">
          <BlTextBody2 :text="item.id" class="text-slate-1000" />
        </div>
        <div class="flex flex-shrink-0 px-4 w-60" :title="item.name">
          <BlTextBody2 :text="item.name" class="truncate text-slate-1000" />
        </div>
      </div>
    </div>
    <div
      class="flex items-center flex-shrink-0 px-4 border-b w-50 bg-pure-white border-slate-400"
    >
      <BlTextBody2 v-if="!item.current_stage_name" text="-" />
      <div v-else class="truncate" :title="item.current_stage_name">
        <BlChip :chip="item.current_stage_name" class="truncate" />
      </div>
    </div>
    <div
      v-if="enableBranch"
      class="flex items-center flex-shrink-0 gap-2 px-4 border-b w-50 bg-pure-white border-slate-400"
    >
      <BlTextBody2 v-if="!item.branch" text="-" />
      <div
        v-else
        class="flex items-center justify-center px-2 truncate rounded h-7 min-w-12"
        :style="{ backgroundColor: item.branch.background_color || 'unset' }"
        :title="item.branch.name"
      >
        <BlTextBody2
          :text="item.branch.name || '-'"
          class="truncate"
          :style="{ color: item.branch.color || 'unset' }"
        />
      </div>
    </div>
    <!--
      "id": 17,
      "name": "Borg",
      "referral": null,
      "prospect_tags": [],
      "date_moved_to_inquiry": null,
      "current_stage_id": 6,
      "current_stage_name": "Discontinued",
      "assigned_user": {
          "id": null,
          "name": null
      },
      "discontinuation_reason": "other",
      "other_discontinuation_reason": "Not",
      "date_moved_to_consultation": "2024-07-02T14:46:56.469Z",
      "date_moved_to_assessment": null,
      "date_moved_to_pending_abc": null,
      "date_moved_to_admitted": null,
      "date_moved_to_discontinued": "2024-07-04T02:48:46.915Z"
     -->
    <div
      class="flex items-center flex-shrink-0 px-4 border-b w-50 bg-pure-white border-slate-400"
    >
      <BlTextBody2
        v-if="item.assigned_user.deleted_at"
        title="Deleted user"
        text="Deleted user"
        class="italic truncate"
      />
      <BlTextBody2
        v-else
        :title="item.assigned_user.name || '-'"
        :text="item.assigned_user.name || '-'"
        class="truncate"
      />
    </div>
    <div
      v-for="stageKey in stageKeys"
      :key="stageKey"
      class="flex items-center flex-shrink-0 px-4 border-b w-50 bg-pure-white border-slate-400"
    >
      <BlTextBody2 :text="displayDate(item[stageKey])" />
    </div>
    <div
      class="flex items-center flex-shrink-0 px-4 border-b w-100 bg-pure-white border-slate-400"
      :title="reasonForDiscontinuedText"
    >
      <BlTextBody2
        :text="reasonForDiscontinuedText"
        class="truncate whitespace-pre-line"
      />
    </div>
    <div
      class="flex items-center flex-shrink-0 px-4 border-b w-50 bg-pure-white border-slate-400"
    >
      <BlTextBody2 :text="`${referralOptions[item.referral] || '-'}`" />
    </div>
    <div
      class="flex items-center flex-shrink-0 gap-2 px-4 border-b w-50 bg-pure-white border-slate-400"
    >
      <BlTextBody2
        v-if="!item.prospect_tags || !item.prospect_tags.length"
        text="-"
      />
      <div
        v-else
        v-for="i in item.prospect_tags.slice(0, 2)"
        :key="i.id"
        class="flex items-center justify-center px-2 truncate rounded h-7 min-w-12 bg-slate-300"
        :title="i.name"
      >
        <BlTextBody2 :text="i.name || '-'" class="truncate" />
      </div>
      <div class="relative" v-if="item.prospect_tags.slice(2).length">
        <div
          class="flex items-center justify-center cursor-pointer"
          @click="moreTags = !moreTags"
        >
          <BlBubbleCount
            :count="`+ ${item.prospect_tags.slice(2).length}`"
            class="h-7 hover:bg-light-purple-100"
          />
        </div>
        <div
          v-if="moreTags"
          class="absolute top-0 right-0 flex flex-col gap-1 p-1 mt-10 truncate border rounded min-w-32 bg-pure-white border-grey-200"
          :style="{ zIndex: 1 }"
        >
          <div
            v-for="(i, idx) in item.prospect_tags.slice(2)"
            :key="idx"
            class="flex max-w-64"
          >
            <div
              :title="i.name || '-'"
              class="px-4 py-2 text-left truncate rounded"
            >
              <BlTextBody2 class="truncate" :text="i.name || '-'" />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-grow pr-6 analytics-last-col-sticky bg-slate-100">
      <div
        class="flex items-center justify-end flex-grow flex-shrink-0 w-32 px-2 border-b border-r bg-pure-white border-slate-400"
        :class="{ 'rounded-br': isLast }"
      >
        <router-link
          :to="
            `/client-acquisition?page=1&per_page=25&name=${item.name}&preview_type=list`
          "
          target="_blank"
          title="View prospect"
        >
          <BlInputButton type="outlined" dense label="View prospect" />
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";

import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlChip from "../../BlChip.vue";
import BlBubbleCount from "../../BlBubbleCount.vue";
import BlInputButton from "../../BlInputButton.vue";

export default {
  name: "RowAcquisitionOverview",
  components: {
    BlTextSubtitle3,
    BlTextBody2,
    BlChip,
    BlBubbleCount,
    BlInputButton
  },
  props: {
    item: { type: Object, required: true },
    isLast: { type: Boolean, default: false }
  },
  data() {
    return {
      moreTags: false,
      discontinuedReasonOptions: {
        fees_too_high: "Fees too high",
        distance_too_far: "Distance too far",
        unable_to_contact_family: "Unable to contact family",
        hours_dont_fit_schedule: "Hours don't fit schedule",
        seeking_service_not_provided: "Seeking services not provided",
        too_old: "Too old",
        resides_in_a_different_state: "Resides in a different state",
        no_show_response: "No show/response",
        do_not_need_therapy: "Do not need therapy",
        ongoing_client: "Ongoing client",
        other: "Other"
      },
      referralOptions: {
        friend_or_family: "Friend/family",
        outside_provider: "Outside provider",
        school_or_daycare: "School/daycare",
        doctor: "Doctor",
        walk_in: "Walk-in",
        website: "Website",
        social_media: "Social media",
        community_event: "Community event",
        other: "Other"
      }
    };
  },
  computed: {
    ...mapGetters(["currentUser", "currentCenter", "stages"]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    stageKeys() {
      return this.stages.map(i => {
        const snakeCase = i.name.toLowerCase().replaceAll(" ", "_");
        return `date_moved_to_${snakeCase}`;
      });
    },
    reasonForDiscontinuedText() {
      if (!this.item) return "-";
      if (!this.item.discontinuation_reason) return "-";
      const reason = this.item.discontinuation_reason;
      if (reason === "other") {
        return this.item.other_discontinuation_reason || "Other";
      } else {
        return this.discontinuedReasonOptions[reason] || "-";
      }
    }
  },
  watch: {
    moreTags(val) {
      if (val) {
        setTimeout(() => {
          window.addEventListener("click", this.closeHandlerTags);
        }, 10);
      }
    }
  },
  methods: {
    displayDate(date, format = "DD/MM/YYYY HH:mm") {
      if (!date) return "-";
      return moment(date).format(format);
    },
    closeHandlerTags() {
      window.removeEventListener("click", this.closeHandlerTags);
      this.moreTags = false;
    }
  }
};
</script>

<style scoped lang="scss">
.analytics-first-col-sticky {
  position: sticky;
  left: 15rem;
  z-index: 1;
}
.analytics-last-col-sticky {
  position: sticky;
  right: 0rem;
  z-index: 1;
}
</style>



// WEBPACK FOOTER //
// src/components/analytics/table/RowAcquisitionOverview.vue